import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaComments, FaHandshake, FaRocket, FaArrowRight } from 'react-icons/fa';

const Section = styled.section`
  padding: 7rem 6%;
  background: linear-gradient(135deg, #111 0%, #222 100%);
  color: white;
  
  @media (max-width: 768px) {
    padding: 5rem 6%;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: white;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
  color: #aaa;
  line-height: 1.7;
  position: relative;
  padding-bottom: 2rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, #fff, rgba(255,255,255,0.3));
    border-radius: 2px;
  }
`;

const ProcessContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0.3), rgba(255,255,255,0.1));
    transform: translateX(-50%);
    z-index: 1;
    
    @media (max-width: 992px) {
      left: 30px;
    }
    
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const ProcessStep = styled(motion.div)`
  display: flex;
  margin-bottom: 6rem;
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 992px) {
    flex-direction: column;
    padding-left: 60px;
  }
  
  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

const StepIndicator = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #333 0%, #444 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  color: white;
  font-size: 2rem;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  
  @media (max-width: 992px) {
    left: 30px;
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
  
  @media (max-width: 768px) {
    position: relative;
    left: 0;
    transform: none;
    margin-bottom: 1.5rem;
  }
`;

const StepContent = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 2.5rem;
  width: 45%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 30px;
    width: 20px;
    height: 20px;
    background: rgba(255, 255, 255, 0.05);
    transform: rotate(45deg);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  ${ProcessStep}:nth-child(odd) & {
    margin-right: auto;
    margin-left: 5%;
    
    &:before {
      right: -10px;
      border-left: none;
      border-bottom: none;
    }
  }
  
  ${ProcessStep}:nth-child(even) & {
    margin-left: auto;
    margin-right: 5%;
    
    &:before {
      left: -10px;
      border-right: none;
      border-top: none;
    }
  }
  
  @media (max-width: 992px) {
    width: 100%;
    margin: 0 !important;
    
    &:before {
      display: none;
    }
  }
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: white;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.75rem;
  }
`;

const StepDescription = styled.p`
  color: #ccc;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const StepFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StepFeature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  color: #ddd;
  
  &:before {
    content: '•';
    color: rgba(255, 255, 255, 0.5);
    font-weight: bold;
    margin-right: 0.75rem;
  }
`;

const ActionButton = styled(motion.button)`
  background: linear-gradient(90deg, #fff, #f0f0f0);
  color: #111;
  border: none;
  padding: 0.9rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin: 2rem auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  svg {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Consultation = () => {
  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Section id="consultation">
      <SectionHeader>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Our Consultation Process
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          We provide a streamlined approach to legal services for startups,
          ensuring you get the support you need at every stage of growth.
        </SectionSubtitle>
      </SectionHeader>
      
      <ProcessContainer>
        <ProcessStep
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <StepIndicator>
            <FaComments />
          </StepIndicator>
          <StepContent>
            <StepTitle>
              <FaComments /> Initial Consultation
            </StepTitle>
            <StepDescription>
              We begin with a comprehensive discussion about your startup's specific needs, challenges, and goals.
            </StepDescription>
            <StepFeatures>
              <StepFeature>In-depth analysis of your current legal position</StepFeature>
              <StepFeature>Identification of potential legal risks</StepFeature>
              <StepFeature>Understanding of your business model and growth plans</StepFeature>
              <StepFeature>FREE 30-minute consultation with no obligations</StepFeature>
            </StepFeatures>
          </StepContent>
        </ProcessStep>
        
        <ProcessStep
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <StepIndicator>
            <FaHandshake />
          </StepIndicator>
          <StepContent>
            <StepTitle>
              <FaHandshake /> Tailored Strategy
            </StepTitle>
            <StepDescription>
              Based on our initial consultation, we develop a customized legal strategy aligned with your business objectives.
            </StepDescription>
            <StepFeatures>
              <StepFeature>Custom legal framework development</StepFeature>
              <StepFeature>Clear deliverables and timelines</StepFeature>
              <StepFeature>Flexible pricing options for startups</StepFeature>
              <StepFeature>Priority access to legal counsel</StepFeature>
            </StepFeatures>
          </StepContent>
        </ProcessStep>
        
        <ProcessStep
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
        >
          <StepIndicator>
            <FaRocket />
          </StepIndicator>
          <StepContent>
            <StepTitle>
              <FaRocket /> Ongoing Support
            </StepTitle>
            <StepDescription>
              We provide continuous legal guidance as your startup grows and evolves in the competitive landscape.
            </StepDescription>
            <StepFeatures>
              <StepFeature>Regular legal updates and compliance checks</StepFeature>
              <StepFeature>Scaling legal support as you grow</StepFeature>
              <StepFeature>International expansion assistance</StepFeature>
              <StepFeature>Strategic legal advice for funding rounds</StepFeature>
            </StepFeatures>
          </StepContent>
        </ProcessStep>
      </ProcessContainer>
      
      <ButtonContainer>
        <ActionButton
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
          whileHover={{ scale: 1.05 }}
          onClick={scrollToContact}
        >
          Book Your Free Consultation <FaArrowRight />
        </ActionButton>
      </ButtonContainer>
    </Section>
  );
};

export default Consultation; 